import { useLoginExternalMutation, useLoginMutation } from '@api/auth';
import { Button } from '@unistocks/components/button/button';
import { HeightAnimator } from '@unistocks/components/layout/height-animator';
import { Page } from '@unistocks/components/page/page';
import { Anchor } from '@unistocks/components/text/link';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { AuthContainer } from '@unistocks/features/authentication/auth-container';
import { AuthType } from '@unistocks/features/authentication/auth-type';
import { ExternalLogin } from '@unistocks/features/authentication/login/external-login';
import { LoginForm, LoginFormData } from '@unistocks/features/authentication/login/login-form';
import { abpTenantFindTenantByName } from '@unistocks/proxy/axios-client/AbpTenantClient';
import { useAppDispatch } from '@unistocks/shared/hooks/redux-hooks';
import { useActiveColorScheme } from '@unistocks/shared/hooks/use-active-color-scheme';
import { t } from '@unistocks/shared/localization/index.ts';
import { AppActions } from '@unistocks/shared/store/root.slice';
import { style } from '@unistocks/shared/styles';
import { AuthTokenDto } from '@unistocks/types/auth';
import { isAxiosError } from 'axios';
import { Link, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { AnimatePresence, isWeb, useMedia, View } from 'tamagui';

export type LoginProps = {
  mode?: AuthType;
};

export const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { setActiveColorScheme } = useActiveColorScheme();
  const media = useMedia();
  const [isAdminMode, setAdminMode] = useState(false);
  const [mode, setMode] = useState<AuthType>('external');
  const isExternal = mode === 'external';

  const handleTokenResponse = (token: AuthTokenDto) => {
    if (token?.access_token) {
      dispatch(AppActions.setToken(token));
      // reset color scheme, so it will be reloaded from the user's profile
      setActiveColorScheme(undefined);

      router.replace(isAdminMode ? '/(admin)' : '/(tabs)');
    }
  };

  const handleErrorResponse = (error: any) => {
    if (isAxiosError(error)) {
      const { data } = error.response || {};

      if (data.error_description) {
        toast.error(data.error_description);
      }
    }
  };

  const { mutateAsync: loginExternal, isPending: isLoadingLoginExternal } =
    useLoginExternalMutation({
      onSuccess: handleTokenResponse,
      onError: handleErrorResponse
    });

  const { mutateAsync: login } = useLoginMutation({
    onSuccess: handleTokenResponse,
    onError: handleErrorResponse
  });

  const submitForm = useCallback(
    async ({ username, password }: LoginFormData) => {
      const loginUsername = username.toLowerCase();

      if (isAdminMode) {
        return login({ username: loginUsername, password });
      }

      const { tenantId } = await abpTenantFindTenantByName(loginUsername);

      if (tenantId) {
        return login({ username: loginUsername, password, tenantId });
      }

      throw new Error(t('AbpUiMultiTenancy::GivenTenantIsNotExist', { 0: loginUsername }));
    },
    [login, isAdminMode]
  );

  const toggle = useCallback(() => {
    setMode(prev => (prev === 'external' ? 'form' : 'external'));
  }, []);

  return (
    <Page.ScrollView
      headerTransparent
      headerLogo={false}
      headerTitle={media.sm ? undefined : t('AbpUi::Login')}
      contentContainerStyle={media.sm ? undefined : style.centerVertical}
    >
      {/* overflow visible for border shadows */}
      <HeightAnimator enabled={!isWeb}>
        <AuthContainer title={t('AbpUi::Login')}>
          <AnimatePresence mode="wait" initial={false}>
            {isExternal ? (
              <View
                key={'external'}
                transition={'200ms'}
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              >
                <ExternalLogin
                  isSubmitting={isLoadingLoginExternal}
                  onSubmit={loginExternal}
                  toggle={toggle}
                />
              </View>
            ) : (
              <View
                key={'form'}
                transition={'200ms'}
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              >
                <LoginForm onSubmit={submitForm} toggle={toggle} />
              </View>
            )}
          </AnimatePresence>

          <Paragraph textAlign={'center'} mt={'$5'} size={'$6'}>
            {t('AbpAccount::AreYouANewUser')}
          </Paragraph>

          <Link asChild href={'/(auth)/register'}>
            <Anchor textAlign={'center'} fontWeight={'bold'} size={'$5'}>
              {t('AbpUi::Register')}
            </Anchor>
          </Link>

          {isExternal ? null : (
            <Button
              size={'$2'}
              variant={'link'}
              justifyContent={'flex-start'}
              ml={'auto'}
              onPress={() => setAdminMode(val => !val)}
            >
              {isAdminMode ? t('AbpAccount::SignInAsTenant') : t('AbpAccount::SignInAsAdmin')}
            </Button>
          )}
        </AuthContainer>
      </HeightAnimator>
    </Page.ScrollView>
  );
};

Login.displayName = 'Login';
